import * as React from 'react'
import styled from 'styled-components'

import * as consent from '@owl-nest/consent'
import { t } from '@owl-nest/localize'
import * as model from '@owl-nest/models'
import * as plume from '@ulule/owl-kit-components/next'

import * as UFE from '../../../../UFE'
import { useStep } from '../useStep'

const VIDEO_ID = 'welcome-video'

// declare that a global YT variable exists (will be loaded elsewhere if needed)
declare const YT: { Player: new (id: string, config: any) => unknown }

type WelcomProps = {
  project: model.project.ProjectForOwner
}

export function Welcome({ project }: WelcomProps): React.ReactElement<any> {
  const video = model.i18n.get(UFE.VIDEOS.onboardingWelcome.version)
  const videoHtml = `<iframe id="${VIDEO_ID}" allow="accelerometer; autoplay; encrypted-media;" src="${video}&enablejsapi=1&showinfo=0&autohide=1&autoplay=1&controls=1&fs=0&cc_load_policy=1" frameBorder="0" allowFullScreen />`
  const videoPlaceholder = model.i18n.get(UFE.VIDEOS.onboardingWelcome.placeholder)

  const playerRef = React.useRef<unknown | null>(null)

  const { setStepValue } = useStep(project)

  return (
    <Wrapper>
      <Title>
        <plume.glyphs.decoration.Quote size={14} />
        <plume.styles.heading.XL>{t('Welcome!')}</plume.styles.heading.XL>
      </Title>
      <plume.styles.heading.XXS>
        {t(
          'We are very happy to have you among us. Jess will explain to you in 1 minute how it works and the next steps:',
        )}
      </plume.styles.heading.XXS>
      <VideoClickWrapper onClick={handleVidoClick}>
        <plume.VideoLoader
          consentWall={consent.media.Wall}
          placeholder={videoPlaceholder?.['x1'] ?? ''}
          srcSet={`${videoPlaceholder?.['x1']} 2x, ${videoPlaceholder?.['x3']} 3x`}
          videoHtml={videoHtml}
        />
      </VideoClickWrapper>
      <plume.Button kind="primary" onClick={handleDone}>
        {t('Start')}
      </plume.Button>
    </Wrapper>
  )

  function handleDone(): void {
    setStepValue()
  }

  function handleVidoClick(): void {
    if (playerRef.current !== null) {
      return
    }

    setTimeout(() => {
      playerRef.current = new YT.Player(VIDEO_ID, {
        events: {
          onStateChange: onPlayerStateChange,
        },
      })
    })
  }

  function onPlayerStateChange(state: { data: number }): void {
    const hasVideoEnded = state.data === 0
    if (hasVideoEnded) {
      handleDone()
    }
  }
}

const Title = styled.div`
  position: relative;

  ${plume.glyphs.decoration.Quote} {
    position: absolute;
    left: -11px;
    top: 15px;
  }
`

const VideoClickWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  max-width: 550px;
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  ${plume.VideoLoader} {
    width: 100%;
  }

  ${plume.styles.heading.XL}, ${plume.styles.heading.XXS} {
    margin-bottom: 24px;
  }

  ${plume.styles.heading.XL} {
    margin-top: 16px;
  }

  ${plume.Button} {
    margin: 48px 0 32px;
    align-self: center;

    @media screen and ${plume.BREAKPOINTS.TABLET} {
      margin: 32px 0 16px;
    }
  }
`
